import gql from 'graphql-tag';
import { Layout } from '../../components';
import React from 'react';
import { useQuery, useMutation } from 'react-apollo';
import { RouteComponentProps } from 'react-router';
import { LayoutContent } from '../../components/LayoutContent';
import { Navigation } from '../../components/Navigation';
import { TopBarDatePicker } from '../../components/TopBarDatePicker';
import { TopBarDropdown } from '../../components/TopBarDropdown';
import { useStartAndEnd } from '../../hooks';
import { DashboardQuery } from './__generated__/DashboardQuery';
import { AcknowledgeUpdates, AcknowledgeUpdatesVariables } from './__generated__/AcknowledgeUpdates';
import { Home } from './Home';

type RouterProps = RouteComponentProps<{ venueId: string }>;

interface HomeProps extends RouterProps {
  data?: DashboardQuery;
  loading: boolean;
  error: Error | null;
  start: Date;
  end: Date;
  interval: string;
  venueId: string;
  acknowledgeUpdates({ variables: AcknowledgeUpdatesVariables });
}

class HomeUnwrapped extends React.Component<HomeProps> {
  handleRevenueIntervalChange = (interval) => {
    const { start, end, venueId } = this.props;
    const url = `/venue/${venueId}/dashboard?start=${start.toISOString()}&end=${end.toISOString()}&interval=${interval.toLowerCase()}`;
    this.props.history.push(url);
  };

  handleDatePickerChange = (start, end) => {
    const { interval, venueId } = this.props;
    this.props.history.push(
      `/venue/${venueId}/dashboard?start=${start.toISOString()}&end=${end.toISOString()}&interval=${interval}`
    );
  };

  public onChangeVenue = ({ id }) => {
    this.props.history.push(`/venue/${id}/dashboard`);
  };

  render() {
    const { start, end, data, acknowledgeUpdates } = this.props;
    const multi = !!(data && data.venues && data.venues.length > 1);

    return (
      <Layout>
        <Navigation />
        <Layout.TopBar>
          <Layout.TopBar.Left>
            {!multi && data && data.venue && <Layout.TopBar.Title>{data.venue.name}</Layout.TopBar.Title>}
            {multi && (
              <TopBarDropdown
                label={data.venue.name}
                leftBorder={false}
                options={data.venues}
                onSelect={this.onChangeVenue}
              />
            )}
          </Layout.TopBar.Left>
          <Layout.TopBar.Right>
            <TopBarDatePicker start={start} end={end} onSubmit={this.handleDatePickerChange} />
          </Layout.TopBar.Right>
        </Layout.TopBar>
        <LayoutContent error={this.props.error}>
          <Home
            start={start}
            end={end}
            onChangeInterval={this.handleRevenueIntervalChange}
            interval={this.props.interval}
            loading={this.props.loading}
            data={data}
            acknowledgeUpdates={acknowledgeUpdates}
          />
        </LayoutContent>
      </Layout>
    );
  }
}

const query = gql`
  query DashboardQuery($venueId: ID!, $start: String!, $end: String!, $interval: String!, $timezoneOffset: Int) {
    venues {
      id
      name
    }
    venue(id: $venueId) {
      id
      name
    }
    dashboardStats(venueId: $venueId, start: $start, end: $end, interval: $interval, timezoneOffset: $timezoneOffset) {
      categories {
        name
        total
      }
      drinks {
        name
        total
      }
      revenue {
        date
        amount
        hidePoint
      }
      spend
      tips
      todaysSales {
        accepted
        nonAccepted
        preorders
      }
      users
    }
    productUpdates {
      id
      releaseDate
      releaseNotes
    }
  }
`;

const acknowledgeUpdatesMutation = gql`
  mutation AcknowledgeUpdates($updateIds: [ID!]!) {
    acknowledgeUpdates(ids: $updateIds)
  }
`;

export const Dashboard = (props: RouterProps) => {
  const { venueId } = props.match.params;
  const { start, end, interval } = useStartAndEnd();
  const variables = {
    venueId,
    start: start.toISOString(),
    end: end.toISOString(),
    interval,
    timezoneOffset: Math.round(start.getTimezoneOffset() / 60) * -1,
  };

  const [acknowledgeUpdates] = useMutation<AcknowledgeUpdates, AcknowledgeUpdatesVariables>(acknowledgeUpdatesMutation);
  const { loading, error, data } = useQuery(query, {
    variables,
  });

  return (
    <HomeUnwrapped
      {...props}
      start={start}
      end={end}
      interval={interval}
      loading={loading}
      error={error}
      data={data}
      venueId={venueId}
      acknowledgeUpdates={acknowledgeUpdates}
    />
  );
};
